import { Box, Typography } from "@mui/material";
import { Fragment } from "react";
import { capsSx, valueSx } from "@/pages/sawlines/components/primitives";
import { formatMillLengthIn } from "@/pages/sawlines/utils/entryBarcode";
import { calcSawFields } from "@/pages/workOrders/config/assetFieldConfig";
import type { SawEntryWorkOrder } from "../repositories/sawEntryQueueRepository";
import { materialText } from "./MaterialQueueRow";

const material = (wo: SawEntryWorkOrder) =>
	materialText(
		wo.SectionName,
		wo.GradeName,
		formatMillLengthIn(wo.MillLength == null ? null : wo.MillLength * 12),
	);

// needed = the WO modal's own # MLB, not a second formula; loaded = tags that went into this WO and matched it
const millBundles = (wo: SawEntryWorkOrder) => {
	const calc = calcSawFields(wo, wo.Quantity);
	return calc?.numMLB == null
		? null
		: {
				loaded: wo.MillBundlesLoaded,
				needed: calc.numMLB,
				each: `${calc.pcsPerMillBundle} pcs each (${wo.MillPcB} × ${calc.cutsPerBundle} cuts)`,
			};
};

// What the saw should be fed: running WO big, next one on a line below.
// Portrait: WO · Part · Material over Mill bundles · Progress; wide: one row.
export const WorkOrderStrip = ({
	running,
	next,
}: {
	running?: SawEntryWorkOrder;
	next?: SawEntryWorkOrder;
}) => {
	const runningMb = running && millBundles(running);
	const nextMb = next && millBundles(next);
	return (
		<Box sx={{ px: 2, py: 1.25 }}>
			{running ? (
				<Box
					sx={{
						display: { xs: "grid", md: "flex" },
						gridTemplateColumns: "repeat(3, auto)",
						justifyContent: "space-between",
						columnGap: 3,
						rowGap: 1,
					}}
				>
					{[
						["Running", `WO ${running.WorkOrderNumber}`],
						["Part", running.PartNumber],
						["Material", material(running)],
						[
							"Mill bundles",
							runningMb ? `${runningMb.loaded} / ${runningMb.needed}` : "—",
						],
						["Progress", `${running.PiecesCut} / ${running.Quantity} pcs`],
					].map(([label, value], index) => (
						<Box key={label}>
							<Typography
								sx={{
									...capsSx,
									...(index === 0 && { color: "primary.main" }),
								}}
							>
								{label}
							</Typography>
							<Typography sx={valueSx}>{value}</Typography>
						</Box>
					))}
				</Box>
			) : (
				<Typography color="text.secondary" sx={{ fontSize: 18 }}>
					No work order running
				</Typography>
			)}
			<Box
				sx={{
					mt: 1,
					pt: 0.75,
					borderTop: 1,
					borderColor: "divider",
					display: "flex",
					flexWrap: "wrap",
					fontSize: 13,
					color: "text.secondary",
					"& > span": { whiteSpace: "nowrap" },
					"& > span + span::before": { content: '"·"', mx: 0.75 },
				}}
			>
				<Box sx={{ ...capsSx, mr: 1 }}>Up next</Box>
				{next ? (
					<>
						<span>WO {next.WorkOrderNumber}</span>
						<span>{next.PartNumber}</span>
						<span>{material(next)}</span>
						<span>{next.Quantity} pcs</span>
						{nextMb && <span>{nextMb.needed} mill bundles</span>}
					</>
				) : (
					<span>No work order planned</span>
				)}
			</Box>
		</Box>
	);
};

// One WO the Loaded prompt will complete or start.
export const WorkOrderSummary = ({
	action,
	workOrder,
}: {
	action: "Complete" | "Start";
	workOrder: SawEntryWorkOrder;
}) => {
	const mb = millBundles(workOrder);
	return (
		<Box
			sx={{
				border: 1,
				borderColor: "divider",
				borderLeft: 6,
				borderLeftColor: action === "Start" ? "success.main" : "grey.500",
				borderRadius: 1,
				px: 2,
				py: 1.5,
			}}
		>
			<Typography
				sx={{
					...capsSx,
					fontSize: 13,
					mb: 1,
					color: action === "Start" ? "success.dark" : "text.secondary",
				}}
			>
				{action} work order
			</Typography>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "auto 1fr",
					columnGap: 2.5,
					rowGap: 0.5,
					alignItems: "baseline",
				}}
			>
				{[
					["Work order", workOrder.WorkOrderNumber],
					["Product", workOrder.PartNumber],
					["Material", material(workOrder)],
					...(mb
						? [
								[
									"Mill bundles",
									action === "Start"
										? `${mb.needed} needed · ${mb.each}`
										: `${mb.loaded} / ${mb.needed} loaded · ${mb.each}`,
								],
							]
						: []),
					["Pieces", `${workOrder.PiecesCut} / ${workOrder.Quantity} pcs`],
				].map(([label, value]) => (
					<Fragment key={label}>
						<Typography sx={capsSx}>{label}</Typography>
						<Typography sx={{ ...valueSx, fontSize: 18 }}>{value}</Typography>
					</Fragment>
				))}
			</Box>
		</Box>
	);
};
